import { html } from 'htm/preact';
import { NationalSummary } from '@utils/eclipse2026';

/**
 * "Como observar o eclipse em segurança" — quatro cards em grelha 2x2.
 *
 * A copy é editorial e vive aqui, não no JSON gerado: não depende de nenhum cálculo
 * e um editor não a deve poder mudar sem review (é um texto de segurança). A única
 * excepção são os números de ocultação do primeiro card, que vêm de `event.national`
 * como em toda a peça — nunca escritos à mão.
 *
 * Dois desvios deliberados face ao desenho do Figma, ambos por não estarem fechados:
 *
 * 1. **Sem o badge "Direção: Oeste-Noroeste (aprox. 281°)".** O azimute do máximo
 *    varia só ~2° em todo o país (280°-282°), o que cai exactamente na fronteira
 *    entre "Oeste" e "Oeste-Noroeste"; qual das duas formulações usar em pt-PT ainda
 *    não foi decidido com a redacção. Fica "a oeste", que é verdade nas duas leituras.
 * 2. **Sem "a costa portuguesa é o local ideal".** Vale para a costa oeste, mas é
 *    falso na costa sul do Algarve, onde o mar fica a sul e o Sol põe-se sobre terra.
 *    Uma dica de observação que falha numa região inteira não deve ser publicada como
 *    regra geral.
 */

/**
 * O `id` do card "Nunca olhe diretamente para o Sol" — um endereço PÚBLICO, feito para
 * se poder ligar a este card de fora do artigo
 * (`/interativo/eclipse-solar-2026/#nunca-olhe-diretamente-para-o-sol`).
 *
 * Constante exportada, e não uma string escrita no meio do markup, porque quem faz o
 * scroll é o {@see App} — se as duas se separarem, o link deixa de funcionar em
 * silêncio, sem erro nenhum a assinalá-lo.
 *
 * **Não mudar sem contar links já partilhados.** Um `id` publicado é uma promessa: um
 * URL que alguém já mandou por email não avisa ninguém quando deixa de aterrar no
 * sítio certo.
 *
 * Vive no `<article>` e não no `<h3>` para o salto trazer o card inteiro em vez de
 * deixar o título encostado ao topo do ecrã. O `tabindex="-1"` que o acompanha não o
 * põe na ordem de tabulação — só o torna focável por código, para quem chega pelo
 * link continuar a navegar a partir daqui e não do topo da página.
 */
export const SAFETY_ANCHOR_ID = 'nunca-olhe-diretamente-para-o-sol';

export function SafetySection({ national }: { national: NationalSummary }) {
    const range = `${national.obscurationPct.minLabel} a ${national.obscurationPct.maxLabel}`;

    return html`
        <section class="eclipse-safety" aria-labelledby="eclipse-safety-title">
            <h2 class="eclipse-safety__title" id="eclipse-safety-title">Como observar o eclipse em segurança</h2>

            <div class="eclipse-safety__grid">
                <article class="eclipse-card eclipse-card--danger" id=${SAFETY_ANCHOR_ID} tabindex="-1">
                    <h3 class="eclipse-card__heading">
                        <span class="eclipse-card__emoji" aria-hidden="true">⚠️</span>
                        <span>Nunca olhe diretamente para o Sol</span>
                    </h3>
                    <p class="eclipse-pill">Obrigatório</p>
                    <p class="eclipse-card__text">É indispensável usar óculos com certificação <strong>ISO 12312-2</strong> durante todas as fases parciais — ou seja, durante todo o eclipse em Portugal, mesmo que o Sol só fique tapado a 100% numa pequena zona de Bragança e no restante território a ocultação seja de ${range}.</p>
                    <p class="eclipse-card__label">O que NÃO serve</p>
                    <p class="eclipse-card__text">Óculos de sol normais, radiografias, CDs, vidro fumado ou negativos fotográficos. Não protegem a retina e podem causar perda de visão permanente.</p>
                </article>

                <article class="eclipse-card">
                    <h3 class="eclipse-card__heading">
                        <span class="eclipse-card__emoji" aria-hidden="true">🧭</span>
                        <span>Procure um horizonte desimpedido a oeste</span>
                    </h3>
                    <p class="eclipse-card__text">No momento do máximo o Sol está a apenas <strong>9° a 11° acima do horizonte</strong> — muito baixo. Um prédio, uma encosta ou uma linha de árvores nessa direção bastam para tapar o eclipse por completo.</p>
                    <p class="eclipse-card__label">Antes do dia</p>
                    <p class="eclipse-card__text">Veja o pôr do sol na véspera a partir do sítio onde pensa ficar. Se vê o Sol pôr-se sem obstáculos, o local serve.</p>
                </article>

                <article class="eclipse-card">
                    <h3 class="eclipse-card__heading">
                        <span class="eclipse-card__emoji" aria-hidden="true">✨</span>
                        <span>O que vai notar perto do máximo</span>
                    </h3>
                    <p class="eclipse-card__label">Luz e temperatura</p>
                    <p class="eclipse-card__text">A luz ganha um tom metálico pouco habitual, as sombras ficam mais nítidas do que é normal ao final da tarde, e a temperatura desce alguns graus. Nas zonas de maior ocultação — o norte e o interior — o céu escurece o suficiente para Vénus se tornar visível a olho nu antes do pôr do sol.</p>
                    <p class="eclipse-card__label">Vénus</p>
                    <p class="eclipse-card__text">Nas zonas de maior ocultação — o norte e o interior — o céu escurece o suficiente para Vénus se tornar visível a olho nu antes do pôr-do-sol.</p>
                </article>

                <article class="eclipse-card">
                    <h3 class="eclipse-card__heading">
                        <span class="eclipse-card__emoji" aria-hidden="true">📷</span>
                        <span>Cuidado com a câmara do telemóvel</span>
                    </h3>
                    <p class="eclipse-card__text">Apontar o telemóvel — ou qualquer câmara de lente aberta — diretamente para o Sol pode danificar o sensor e a lente de forma permanente dos aparelhos.</p>
                    <p class="eclipse-card__label">A solução</p>
                    <p class="eclipse-card__text">Ponha um filtro solar (que pode comprar nas lojas de tecnologia e fotografia) à frente da lente antes de fotografar. E lembre-se de que os óculos protegem os seus olhos, não a câmara.</p>
                </article>
            </div>
        </section>
    `;
}
